import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Disc3, Upload, Sparkles, Music2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HotBadge from "../components/HotBadge";
import { SkeletonCDGrid } from "../components/Skeleton";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VitrineCDsPage() {
  const [cds, setCds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/cd-submissions/public`)
      .then(r => r.json())
      .then(data => { setCds(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Top 5 CDs por plays (mapa id → rank 1..5)
  const hotRanks = useMemo(() => {
    const sorted = [...cds].filter(c => (c.plays || 0) > 0).sort((a, b) => (b.plays || 0) - (a.plays || 0));
    const map = {};
    sorted.slice(0, 5).forEach((cd, i) => { map[cd.id] = i + 1; });
    return map;
  }, [cds]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF007F]/15 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/30 text-[#FF007F] text-xs font-bold mb-4">
                <Sparkles size={14} /> CDs DA COMUNIDADE
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-3">
                Vitrine de <span className="text-[#FF007F] italic">CDs</span>.
              </h1>
              <p className="text-zinc-400 text-lg max-w-2xl">
                Discos enviados por artistas independentes do Arrocha. Aprovados pela equipe arrochaplay.
              </p>
            </div>
            <Link to="/envie-seu-cd"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all whitespace-nowrap shadow-lg shadow-[#FF007F]/20"
              data-testid="send-cd-cta">
              <Upload size={18} /> Envie seu CD
            </Link>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        {loading ? (
          <SkeletonCDGrid count={10} />
        ) : cds.length === 0 ? (
          <div className="text-center py-20 bg-[#0F0F0F] border border-white/10 rounded-3xl">
            <Disc3 className="w-20 h-20 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Seja o primeiro!</h3>
            <p className="text-zinc-500 mb-6 max-w-md mx-auto">A vitrine ainda está vazia. Envie seu CD e seja o primeiro artista independente do arrochaplay.</p>
            <Link to="/envie-seu-cd" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all">
              <Upload size={18} /> Enviar meu CD
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
            {cds.map(cd => (
              <Link key={cd.id} to={`/cd/${cd.id}`}
                className="group bg-[#0F0F0F] border border-white/5 hover:border-[#FF007F]/40 rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
                data-testid={`vitrine-cd-${cd.id}`}>
                <div className="aspect-square overflow-hidden bg-black relative">
                  {hotRanks[cd.id] ? <HotBadge rank={hotRanks[cd.id]} /> : null}
                  <img src={`${API}${cd.cover_url}`} alt={cd.cd_title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur px-2 py-1 rounded-full text-xs flex items-center gap-1">
                    <Music2 size={11} /> {cd.tracks_count ?? cd.tracks?.length ?? 0}
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs text-[#FF007F] uppercase tracking-wider truncate font-bold">{cd.artist_name}</p>
                  <h3 className="font-semibold text-white truncate mt-0.5">{cd.cd_title}</h3>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
